/**
 * The one place a client is constructed. Mock mode: `?mock=1`, `VITE_MOCK_API=1` or `MOCK_API` in config.js.
 * Tests call `setApiClient(createMockClient({ latencyMs: 0 }))`.
 */
import { getRuntimeConfig } from '../config';
import { createHttpClient } from './client';
import type { WindropClient } from './types';

let client: WindropClient | null = null;

/**
 * Wraps a client that is loaded on demand: the mock (fixtures + contracts runtime + zod) is a separate chunk
 * that production sessions never download (scripts/check-bundle.mjs budget).
 */
export function createLazyClient(load: () => Promise<WindropClient>): WindropClient {
  let pending: Promise<WindropClient> | null = null;
  const get = () => (pending ??= load());
  return new Proxy({} as WindropClient, {
    get:
      (_t, prop: keyof WindropClient) =>
      (...args: unknown[]) =>
        get().then((c) => (c[prop] as (...a: unknown[]) => unknown)(...args)),
  });
}

export function getApi(): WindropClient {
  if (!client)
    client = getRuntimeConfig().mockApi
      ? createLazyClient(() => import('./mock').then((m) => m.createMockClient()))
      : createHttpClient();
  return client;
}

/** Test / dev-kit hook. */
export function setApiClient(next: WindropClient | null): void {
  client = next;
}

export { ApiError, NetworkError } from './types';
export type { WindropClient, RequestOptions, WebVitalsBeacon } from './types';
export { createHttpClient } from './client';
export * from './constants';
export { queryKeys } from './keys';
